/**
 * Fonctionnalités disponibles selon le segment de l’établissement (éducation / entreprise).
 *
 * Source unique utilisée par les gardes de route, les onglets `/institution`, la barre latérale
 * et les accès rapides du tableau de bord. Le segment est calculé par le backend
 * (`apps/backend/src/utils/institution-segment.ts`) et renvoyé dans la session :
 * - établissement sans groupe multi-site → `education` ;
 * - groupe de segment `enterprise` → `enterprise` ;
 * - `null` uniquement pour un compte sans établissement (indépendant, admin plateforme) : aucune restriction.
 *
 * Les API v2 RH répondent 403 `SEGMENT_NOT_AVAILABLE` pour l’éducation : le front masque ces écrans en amont.
 */
import { INSTITUTION_MENU_FLAGS, type InstitutionMenuFlagKey } from '@/lib/institution-menu-flags';

export type InstitutionSegment = 'education' | 'enterprise';
export type InstitutionSegmentOrNone = InstitutionSegment | null;

/** Contexte i18next (`t(key, { context })` → clé `key_education`) ; `undefined` hors éducation. */
export type InstitutionSegmentContext = 'education' | undefined;

/** Fonctionnalités hors drapeaux de menu (routes directes du module RH / manager / collaborateur). */
export const SEGMENT_FEATURES = {
  /** `/team`, `/reviews` : espace manager (entretiens, équipe). */
  manager_space: 'manager_space',
  /** `/training`, `/skills` : espace collaborateur (plan de formation, compétences). */
  collaborator_space: 'collaborator_space',
  /** `/reports` : rapports RH (exports XLSX / CSV, planification). */
  rh_reports: 'rh_reports',
  /** Conformité Qualiopi / BPF / CPF / OPCO. */
  rh_compliance: 'rh_compliance',
  /** Bouton « Référentiel » (compétences RH) dans les évaluations de classe. */
  competency_framework: 'competency_framework',
} as const;

export type SegmentFeatureKey =
  | InstitutionMenuFlagKey
  | (typeof SEGMENT_FEATURES)[keyof typeof SEGMENT_FEATURES];

/** Modules RH / entreprise sans objet dans une école. */
const HIDDEN_FOR_EDUCATION: ReadonlySet<string> = new Set<string>([
  INSTITUTION_MENU_FLAGS.webinars_enterprise,
  INSTITUTION_MENU_FLAGS.rh_campaigns,
  INSTITUTION_MENU_FLAGS.rh_codir,
  INSTITUTION_MENU_FLAGS.rh_competencies,
  INSTITUTION_MENU_FLAGS.rh_catalog,
  INSTITUTION_MENU_FLAGS.integrations_page,
  INSTITUTION_MENU_FLAGS.tab_rh_dashboard,
  INSTITUTION_MENU_FLAGS.tab_infpc,
  INSTITUTION_MENU_FLAGS.tab_sirh,
  INSTITUTION_MENU_FLAGS.tab_services,
  SEGMENT_FEATURES.manager_space,
  SEGMENT_FEATURES.collaborator_space,
  SEGMENT_FEATURES.rh_reports,
  SEGMENT_FEATURES.rh_compliance,
  SEGMENT_FEATURES.competency_framework,
]);

/**
 * Entreprise : aucune fonctionnalité retirée par le segment (classes, évaluations de classe, référentiel…
 * restent pilotés par les drapeaux de menu de l’établissement).
 */
const HIDDEN_FOR_ENTERPRISE: ReadonlySet<string> = new Set<string>();

/** Normalise la valeur de session (toute autre valeur → pas de segment). */
export function normalizeInstitutionSegment(raw: unknown): InstitutionSegmentOrNone {
  return raw === 'education' || raw === 'enterprise' ? raw : null;
}

/** `true` si la fonctionnalité existe pour ce segment (sans segment : tout est permis). */
export function isAllowedForSegment(featureKey: string, segment: InstitutionSegmentOrNone | undefined): boolean {
  if (segment === 'education') return !HIDDEN_FOR_EDUCATION.has(featureKey);
  if (segment === 'enterprise') return !HIDDEN_FOR_ENTERPRISE.has(featureKey);
  return true;
}

/** Contexte i18next à passer à `t()` pour obtenir le vocabulaire scolaire (Élèves, Cours, Enseignants…). */
export function segmentI18nContext(segment: InstitutionSegmentOrNone | undefined): InstitutionSegmentContext {
  return segment === 'education' ? 'education' : undefined;
}
